import dayjs from 'dayjs';
import { CONSTANTS } from './constants.js';
import { utilities } from './utilities.js';
import { SharePointList } from './sp-classes.js';

class AppsUsageItem {
	constructor(data) {
		this.id = data.Id;
		this.appCode = data.AppCode;
		// eslint-disable-next-line no-use-before-define
		this.data = JSON.parse(data.Data).map((d) => new AppUsageSession(d));
		this.dateYMD = data.DateYMD;
		// eslint-disable-next-line no-undef
		this._userId = data.UserId;
	}

	get user() {
		return AppsUsageItem.spUsers.find((u) => u.id === this._userId);
	}

	get userId() {
		return this._userId;
	}
}

AppsUsageItem.initialize = ({ spUsers }) => {
	AppsUsageItem.spUsers = spUsers;
};

class AppUsageAction {
	constructor(data) {
		this.time = data.time || dayjs().format('HH:mm:ss');
		this.actionName = data.actionName;
		this.actionData = data.actionData;
		this.usageViewInfo = null;
	}

	setUsageViewInfo(documentationCategories, documentationPackages, documentationSubcategories, series, versions) {
		// eslint-disable-next-line no-use-before-define
		this.usageViewInfo = new AppUsageActionViewInfo(this.getUsageViewInfo(documentationCategories, documentationPackages, documentationSubcategories, series, versions));
	}

	getUsageViewInfo(documentationCategories, documentationPackages, documentationSubcategories, series, versions) {
		let description = '', descriptor = '', family = '', title = '', seriesName = '', versionName = '';

		if (typeof this.actionData.seriesId === 'number') {
			seriesName = this.actionData.seriesId ? series.find((s) => s.id === this.actionData.seriesId).title : 'No series Data';
			versionName = this.actionData.versionId ? versions.find((v) => v.id === this.actionData.versionId).title : 'No version data';
		} else if (Array.isArray(this.actionData.seriesId)) {
			seriesName = series.filter((s) => this.actionData.seriesId.includes(s.id)).map((s) => s.title);
			versionName = versions.filter((v) => this.actionData.versionId.includes(v.id)).map((v) => v.title);
		} else {
			seriesName = this.actionData.seriesName || 'No series Data';
			versionName = this.actionData.seriesVersion || 'No version data';
		}

		switch (this.actionName) {
			case CONSTANTS.UserSettings.fieldsKeys.releaseNotes:
				// eslint-disable-next-line no-case-declarations
				const numPages = Object.keys(this.actionData).reduce((acum, action) => (action.startsWith('timeInRelease') ? ++acum : acum), 0),
					notesText = numPages > 1 ? 'notes' : 'note',
					{ hours, minutes, seconds } = utilities.convertMsToHM(this.actionData.totalTimeInModal),
					hoursText = hours > 0 ? `${hours}h` : '',
					minutesText = (minutes > 0 || hours > 0) ? `${minutes}'` : '',
					secondsText = `${seconds}''`,
					releaseFinished = this.actionData.dateTutorialCompleted ? 'Tutorial completed' : 'Tutorial not completed';

				description = `${releaseFinished} (${numPages} ${notesText}, ${hoursText} ${minutesText} ${secondsText})`;

				descriptor = '[ReleaseNotes] ';

				break;

			case CONSTANTS.UserSettings.fieldsKeys.generalFilters:
				switch (this.actionData.type) {
					case 'searchForUpdates':
						description = `SearchForUpdates: ${this.actionData.familyInfo.searchForUpdates ? 'ON' : 'OFF'}`;
						break;
					case 'displayDiscontinuedProducts':
						description = `DiscontinuedSeries: ${this.actionData.familyInfo.displayDiscontinuedProducts ? 'ON' : 'OFF'}`;
						break;
					case 'displayFamily':
						description = `DisplayFamily: ${this.actionData.familyInfo.isDisplayed ? 'ON' : 'OFF'}`;
						break;
					default:
						description = 'General Filters - Case per definir';
						break;
				}
				descriptor = '[GeneralViewFilters] ';
				family = `${this.actionData.familyInfo.title} `;
				break;

			case CONSTANTS.UserSettings.fieldsKeys.documentLinksFilter:
				if (this.actionData.checkAllClicked) description = `Check sub/categories: ${this.actionData.value ? 'ALL' : 'NONE'}`;

				else if (this.actionData.okButtonClicked) {
					let languageText = this.actionData.allLanguagesSelected ? 'ALL' : `${this.actionData.documentationLanguagesDisplayedTitle}`,
						subcateriesText = this.actionData.subcategoriesDisplayed.length === documentationSubcategories.length ? 'ALL' : `${this.actionData.subcategoriesDisplayed.length} of ${documentationSubcategories.length}`,
						defaultPackage = '';

					description = `Languages: [${languageText}]. Subcategories: [${subcateriesText}]`;

					if (this.actionData.subcategoriesDisplayed.length !== documentationSubcategories.length) {
						defaultPackage = documentationPackages.find((dp) => JSON.stringify(dp.subcategoriesIds.sort()) === JSON.stringify(this.actionData.subcategoriesDisplayed.sort()));
						if (defaultPackage) {
							title += `Package: ${defaultPackage.title}\n`;
						}
						title += `Subcategories:\n  -${this.actionData.subcategoriesDisplayed.map((scd) => documentationSubcategories.find((sc) => sc.id === scd).fullTitle).sort().join('\n  -')}`;
					}
				} else if (this.actionData.subcategoryQuickFilter) description = `Subcategory hidden: ${documentationSubcategories.find((sc) => sc.id === this.actionData.id).title}`;

				else if (this.actionData.defaultPackageInfo) description = `Default filter package: ${this.actionData.defaultPackageInfo.title}`;

				else if (this.actionData.categoryQuickFilter) description = `Category hidden: ${documentationCategories.find((c) => c.id === this.actionData.id).title}`;

				else description = 'Document link filters - Case per definir';

				descriptor = '[ProductViewFilters] ';
				break;

			case CONSTANTS.UserSettings.fieldsKeys.showHistoryVersion:
				descriptor = '[DocumentVersionHistory] ';
				description = `${this.actionData.documentName} in '${seriesName}' (${versionName})`;
				break;

			case CONSTANTS.UserSettings.fieldsKeys.favorite:
				descriptor = '[DocumentFavorite] ';
				description = `${this.actionData.documentName} in '${seriesName}' (${versionName})`;
				break;

			case CONSTANTS.UserSettings.fieldsKeys.copyLinkToClipboard:
				descriptor = '[DocumentCopyLinkToClipboard] ';
				description = `${this.actionData.documentName} in '${seriesName}' (${versionName})`;
				break;

			case CONSTANTS.UserSettings.fieldsKeys.openPDF:
				descriptor = '[DocumentLinkOpenPDF] ';
				description = `${this.actionData.documentName} in '${seriesName}' (${versionName})`;
				break;

			case CONSTANTS.UserSettings.fieldsKeys.editUserNoteComment:
				description = `${this.actionData.documentName} in '${seriesName}' (${versionName}). Comment: ${this.actionData.comment}`;
				descriptor = '[DocumentUserNote] ';
				break;

			case CONSTANTS.UserSettings.fieldsKeys.downloadDocuments:
				description = `${this.actionData.length} ${this.actionData.length > 1 ? 'documents' : 'document'} in '${seriesName}' (${versionName})`;
				descriptor = '[DownloadDocuments] ';
				title = this.actionData.arrayDocumentsTitle ? this.actionData.arrayDocumentsTitle.join('\n') : 'No Data';
				break;

			case CONSTANTS.UserSettings.fieldsKeys.editDocumentLink:
				// eslint-disable-next-line no-case-declarations, one-var
				const { isEdition, isAddition } = this.actionData;

				if (this.actionData.isMultipleEdit) description = `${this.actionData.documentName} in [${seriesName}] and versions [${versionName}]. User used multiple edit`;
				else description = `${this.actionData.documentName} in '${seriesName}' and versions '${versionName}'.`;

				descriptor = (!isEdition && !isAddition) ? '[DocumentLink[Added/Edited]] ' : (isEdition ? '[DocumentLinkEdited] ' : '[DocumentLinkAdded] ');

				break;

			case CONSTANTS.UserSettings.fieldsKeys.openSeries:
				description = `Series '${seriesName}' and version ${versionName}`;
				descriptor = '[GeneralViewOpenSeries] ';

				break;

			default:
				description = `${this.actionName} Case per definir`;
				descriptor = '[No Descriptor] ';

				break;
		}

		return { description, descriptor, family, title };
	}
}

class AppUsageActionViewInfo {
	constructor(data) {
		this.description = data.description;
		this.descriptor = data.descriptor;
		this.family = data.family;
		this.title = data.title;
	}
}

class AppUsageSession {
	constructor(data) {
		this.actions = data.actions.map((a) => new AppUsageAction(a));
		this.browser = data.browser;
		this.publicIp = data.publicIp;
		this.localIp = data.localIp;
		this.uuid = data.uuid;
	}
}



class DisplayedLanguage {
	constructor(data) {
		this.title = data;
		this.isDisplayed = true;
	}
}



class DocumentationLibrary extends SharePointList {
	constructor(isListNotLibrary, siteRelativePaths, internalNames, titles, listItemEntityTypeFullName, effectivePermissions) {
		super(isListNotLibrary, siteRelativePaths, internalNames, titles, listItemEntityTypeFullName, effectivePermissions);
		this._isInternal = this.title().includes('[INTERNAL]');
	}

	get isInternal() {
		return this._isInternal;
	}
}



class DocumentationLinkItem {
	constructor(data) {
		this.id = data.ID || data.id;
		this.title = data.Title || data.title;
		this._authorId = data.AuthorId;
		// eslint-disable-next-line indent
			this.author = null;
		this._editorId = data.EditorId;
		// eslint-disable-next-line indent
			this.editor = null;
		this._languageId = data.DocumentationLanguagesId;
		// eslint-disable-next-line indent
			this.language = null;
		this._subcategoryId = data.DocumentationLinksSubcategoriesId;
		// eslint-disable-next-line indent
			this.subcategory = null;
		this._certificationId = data.DocumentationCertificationId || data.DocumentationCertificationsId;
		// eslint-disable-next-line indent
			this.certification = null;
		this.otherDocuments = !!this._certificationId ? true : false;
		this.comment = null;
		this.description = null;
		this.documentModified = null;
		this.documentVersions = null;
		this.fileSize = null;
		this.json = data.JSON;
		this.isFavorite = false;
		this.isFavoriteInThisSeries = false;
		this.linkModified = data.Modified ? dayjs(data.Modified) : data.linkModified;
		this.pendrive = data.Pendrive;
		this.uniqueId = null;

		this.relink();
	}

	get authorId() {
		return this._authorId;
	}

	set authorId(id) {
		this._authorId = id;
		this.author = DocumentationLinkItem.spUsers.find((u) => u.id === this.authorId);
	}

	get category() {
		return this.subcategory && this.subcategory.category;
	}

	get certificationId() {
		return this._certificationId;
	}

	set certificationId(id) {
		this._certificationId = id;
		this.subcategory = DocumentationLinkItem.subcategories.find((sc) => sc.id === this.subcategoryId);
	}

	get documentName() {
		return this.title;
	}

	get documentUrl() {
		return this.libraryName ? `${DocumentationLinkItem.tenantUrl}${DocumentationLinkItem.siteCollectionPath}/${this.libraryName}/${this.title}.pdf` : null;
	}

	get editorId() {
		return this._editorId;
	}

	set editorId(id) {
		this._editorId = id;
		this.editor = DocumentationLinkItem.spUsers.find((u) => u.id === this.editorId);
	}

	get fileExists() {
		return Boolean(this.documentModified);
	}

	get hasValidFormatTitle() {
		return utilities.isValidDocumentationDocName(this.title) || utilities.isValidM3DocName(this.title);
	}

	get hasWarnings() {
		return Boolean(this.hasWarningsPendrive || this.languageDoesNotMatch);
	}

	get languageEditEnabled() {
		if (this.otherDocuments || this._certificationId) return this.fileExists;
		return Boolean(this.subcategory && !this.subcategory.hasImplicitLanguage && this.fileExists && this.hasValidFormatTitle);
	}

	get languageId() {
		return this._languageId;
	}

	set languageId(id) {
		this._languageId = id;
		this.language = DocumentationLinkItem.languages.find((l) => l.id === this.languageId);
	}

	get languageDoesNotMatch() {
		return this.language.Code !== this.title.substr(5, 2);
	}

	get languagesScreen() {
		return this.language && this.language.languagesScreen;
	}

	get libraryName() {
		if (this.otherDocuments) {
			return 'Altres';
		}
		return utilities.getSingleLibraryNameForDocumentTitle(this.title);
	}

	get linkExists() {
		return Boolean(this.linkModified);
	}

	get previewUrl() {
		return this.getPreviewUrl();
	}

	get size() {
		return utilities.size(this.fileSize);
	}

	get subcategoryId() {
		return this._subcategoryId;
	}

	set subcategoryId(id) {
		this._subcategoryId = id;
		this.subcategory = DocumentationLinkItem.subcategories.find((sc) => sc.id === this.subcategoryId);
	}

	clone() {
		let newItem = new DocumentationLinkItem({
			AuthorId: this.authorId,
			DocumentationLanguagesId: this.languageId,
			DocumentationLinksSubcategoriesId: this.subcategoryId,
			DocumentationCertificationsId: this.certificationId,
			EditorId: this.editorId,
			ID: this.id,
			JSON: this.json,
			Modified: this.linkModified.toISOString(),
			Pendrive: this.pendrive,
			Title: this.title,
		});

		newItem.description = this.description;
		newItem.documentModified = this.documentModified;
		newItem.documentVersions = this.documentVersions;
		newItem.uniqueId = this.uniqueId;

		newItem.comment = this.comment;
		newItem.isFavorite = this.isFavorite;
		newItem.isFavoriteInThisSeries = this.isFavoriteInThisSeries;

		return newItem;
	}

	getDocumentVersionUrl(version) {
		if (version.isCurrentVersion) {
			return this.documentUrl;
		}

		let filenamePosition = this.documentUrl.lastIndexOf('/'),
			docLibFullUrl = this.documentUrl.substr(0, filenamePosition),
			versionUrl = this.documentVersions.find((v) => v.versionId === version.versionId).url,
			namePdf = this.documentUrl.substr(filenamePosition),
			url = docLibFullUrl + versionUrl + namePdf;

		return url;
	}

	getPreviewUrl() {
		let filenamePosition = this.documentUrl.lastIndexOf('/'),
			docLibFullUrl = this.documentUrl.substr(0, filenamePosition),
			tenantUrlLength = DocumentationLinkItem.tenantUrl.length,
			docLibRelativeUrl = this.documentUrl.substr(tenantUrlLength, filenamePosition - tenantUrlLength),
			itemRelativeUrl = this.documentUrl.substr(tenantUrlLength),
			url = `${docLibFullUrl}/Forms/AllItems.aspx?id=${itemRelativeUrl}&parent=${docLibRelativeUrl}&p=16`;
		return url;
	}

	hasSomeLanguageDisplayed(arrayLanguagesDisplayed) {
		return arrayLanguagesDisplayed.length > 0 ? this.language && this.language.includes(arrayLanguagesDisplayed) : true;
	}

	isUpdated(date) {
		return (this.linkModified && this.linkModified.isSameOrAfter(date, 'day'))
				|| (this.documentModified && this.documentModified.isSameOrAfter(date, 'day'));
	}

	relink() {
		this.language = DocumentationLinkItem.languages.find((l) => l.id === this.languageId);
		this.subcategory = DocumentationLinkItem.subcategories.find((sc) => sc.id === this.subcategoryId);
		this.author = DocumentationLinkItem.spUsers.find((u) => u.id === this.authorId);
		this.editor = DocumentationLinkItem.spUsers.find((u) => u.id === this.editorId);
		this.certification = DocumentationLinkItem.documentationCertifications.find((c) => c.id === this.certificationId);
	}
}

DocumentationLinkItem.initialize = ({ subcategories, languages, spUsers, tenantUrl, documentationSitePath, certifications }) => {
	DocumentationLinkItem.documentationCertifications = certifications;
	DocumentationLinkItem.languages = languages;
	DocumentationLinkItem.siteCollectionPath = documentationSitePath;
	DocumentationLinkItem.spUsers = spUsers;
	DocumentationLinkItem.subcategories = subcategories;
	DocumentationLinkItem.tenantUrl = tenantUrl;
};



class DocumentationLinksList {
	constructor(data) {
		this.title = data.Title;
		this.itemCount = data.ItemCount;
		this.listItemEntityTypeFullName = data.ListItemEntityTypeFullName;
		this.effectivePermissions = { high: data.EffectiveBasePermissions.High, low: data.EffectiveBasePermissions.Low };
	}
}



class DocumentationPackage {
	constructor(data) {
		this.id = data.ID;
		this.title = data.Title;
		// using batch calls, subcategoriesIds are not an array, is an object, which includes 2 fields, metadata and results, results contains the desirable array
		this.subcategoriesIds = data.SubcategoriesId.constructor.name === 'Object' ? data.SubcategoriesId.results : data.SubcategoriesId;
		this.languages = data.Languages ? data.Languages.split('/').map((l) => new DisplayedLanguage(l)) : null;
	}
}



class DocumentCategory {
	constructor(c) {
		this.id = c.Id;
		this.title = c.Title;
		this._isDisplayed = !(this.id === CONSTANTS.OtherDocuments.categoryId);
	}

	get isDisplayed() {
		return this._isDisplayed;
	}

	set isDisplayed(value) {
		this._isDisplayed = value;
	}
}



class DocumentCertification {
	constructor(c) {
		this.id = c.Id;
		this.title = c.Title;
		this.isDisplayed = false;
	}
}



class DocumentationCertificationsPackages {
	constructor(c) {
		this.id = c.Id;
		this.title = c.Title;
		this.certificationId = c.CertificatsId.constructor.name === 'Object' ? c.CertificatsId.results : c.CertificatsId;// c.CertificatsId;
		this.subcategoriesIds = c.SubcategoriesId.constructor.name === 'Object' ? c.SubcategoriesId.results : c.SubcategoriesId;
	}
}



class DocumentLanguage {
	constructor(data) {
		this.id = data.Id;
		this.title = data.Title;
		this.code = data.Code;
		this.pendriveCode = data.PendriveCode;
		this.languagesDisplayed = data.OtherLanguagesDisplay;
	}

	get isoName() {
		return this.title;
	}

	get languagesScreen() {
		return this.title.replace(/(\/|\/\/)/gi, '');
	}

	includes(arrayLanguagesDisplayed) {
		return Boolean(this.languagesDisplayed.match(new RegExp(([...arrayLanguagesDisplayed]).join('|'))));
	}
}



class DocumentSubcategory {
	constructor(sc) {
		this.id = sc.Id;
		this.fullTitle = sc.FullTitle;
		this.title = sc.Title;
		this._categoryId = sc.DocumentationLinksCategoriesId;
		// eslint-disable-next-line indent
			this.category = null;
		this.hasImplicitLanguage = sc.HasImplicitLanguage;
		this._isDisplayed = !(this.id === CONSTANTS.OtherDocuments.subcategoryId);
		this.relink();
	}

	get categoryId() {
		return this._categoryId;
	}

	set categoryId(id) {
		this._categoryId = id;
		this.relink();
	}

	get isDisplayed() {
		return this._isDisplayed;
	}

	set isDisplayed(value) {
		this._isDisplayed = value;
		this.relink();
	}

	relink() {
		this.category = DocumentSubcategory.categories.find((c) => c.id === this.categoryId);
	}
}

DocumentSubcategory.initialize = ({ categories }) => {
	DocumentSubcategory.categories = categories;
};



class DocumentVersion {
	constructor(data, isCurrentVersion) {
		this.versionId = data.ID || data.UIVersion;
		this.checkInComment = data.CheckInComment;
		this.description = data.Title || null;
		this._editorId = data.EditorId;
		// eslint-disable-next-line indent
			this.editor = null;
		this.fileSize = data.Size || data.Length;
		this.modified = dayjs(data.Created || data.TimeLastModified);
		this.isCurrentVersion = isCurrentVersion;
	}

	get editorId() {
		return this._editorId;
	}

	set editorId(id) {
		this._editorId = id;
	}

	get size() {
		return utilities.size(this.fileSize);
	}

	get url() {
		return `/_vti_history/${this.versionId}`;
	}

	get versionLabel() {
		return `${Math.floor(this.versionId / 512)}.${this.versionId % 512}`;
	}

	relink() {
		this.editor = DocumentationLinkItem.spUsers.find((u) => u.id === this.editorId);
	}
}
DocumentVersion.initialize = ({ spUsers }) => {
	DocumentVersion.spUsers = spUsers;
};



class ProductFamily {
	constructor(f) {
		this.id = f.Id;
		this.title = f.Title;
		this.name = f.nameID;
		this.canBeSeenByExternalUsers = f.CanBeSeenByExternalUsers;
		this.color = `#${f.Color}`;

		this.displayDiscontinuedProducts = true;
		this.displayOnlyPendriveSeries = false;
		this.isDisplayed = true;
		this.isSearchingForUpdates = false;
		this.searchForUpdates = false;
		this.series = [];
	}

	get dataLocalStorage() {
		return {
			displayDiscontinuedProducts: this.displayDiscontinuedProducts,
			displayOnlyPendriveSeries: this.displayOnlyPendriveSeries,
			isDisplayed: this.isDisplayed,
			searchForUpdates: this.searchForUpdates,
		};
	}
}



class ProductSeries {
	constructor(s) {
		this.id = s.Id;
		this.title = s.Title;
		this.url = s.Url;
		this.shortDescription = s.ShortDescription;
		this._familyId = s.FamilyNameId;
		// eslint-disable-next-line indent
			this.family = null;
		this._statusId = s.StatusId;
		// eslint-disable-next-line indent
			this.status = null;
		this.hasStreamVideos = Boolean(s.HasStreamVideos);

		this.versions = [];

		this.includedInPendrive = false;
		this.contentsLoaded = false;
		// this.hasUpdates = false;

		this.relink();
	}

	get familyId() {
		return this._familyId;
	}

	set familyId(id) {
		this._familyId = id;
		this.relink();
	}

	get hasUpdates() {
		return this.versions.some((v) => v.hasUpdates);
	}

	get isCatalogue() {
		return (this.status.title === 'Catalogue');
	}

	get isDiscontinued() {
		return (this.status.title === 'Discontinued');
	}

	get isEE() {
		return (this.status.title === 'Special Equipment');
	}

	get isUnderDevelopment() {
		return (this.status.title === 'Under Development');
	}

	get statusId() {
		return this._statusId;
	}

	set statusId(id) {
		this._statusId = id;
		this.relink();
	}

	get streamLink() {
		return this.hasStreamVideos ? `https://web.microsoftstream.com/browse?q=${encodeURIComponent(`#${this.url}`)}` : '';
	}

	relink() {
		this.family = ProductSeries.families.find((f) => f.id === this.familyId);
		this.status = ProductSeries.statuses.find((s) => s.id === this.statusId);
	}
}

ProductSeries.initialize = ({ families, statuses }) => {
	ProductSeries.families = families;
	ProductSeries.statuses = statuses;
};



class ProductSeriesStatus {
	constructor(data) {
		this.id = data.Id;
		this.title = data.Title;
	}
}



class ProductVersion {
	constructor(data) {
		this.id = data.Id;
		this.title = data.Title;
		this._seriesId = data.SeriesNameId;
		// eslint-disable-next-line indent
			this.series = null;
		this.listName = data.ListName;
		this.includedInPendrive = data.IncludedInPendrive;

		this.documentationLinksList = null;
		this.slideshowPicturesLibrary = null;
		this.loResPicturesLibrary = null;
		this.hiResPicturesLibrary = null;

		this.hasUpdates = false;

		this.relink();
	}

	get seriesId() {
		return this._seriesId;
	}

	set seriesId(id) {
		this._seriesId = id;
		this.relink();
	}


	relink() {
		this.series = ProductVersion.series.find((s) => s.id === this.seriesId);
	}
}

ProductVersion.initialize = ({ series }) => {
	ProductVersion.series = series;
};



class SPUser {
	constructor(data) {
		this.id = data.Id || 0;
		this.displayName = data.Title || '';
		this.email = data.Email || '';
		this.userPrincipalName = data.UserPrincipalName || '';
		this.isExternal = null;

		this.appUsageItems = [];
	}

	getUsageViewLabelText() {
		const days = this.appUsageItems,
			totalSessions = days.reduce((acum, uti) => acum + uti.data.length, 0),
			totalDays = days.length;
		return `${this.displayName} [${totalDays} days - ${totalSessions} sessions]`;
	}

	getUsageViewUserSummary({ documentationSubcategories, documentationPackages, series, versions, families }) {
		const days = this.appUsageItems.length,
			prova = this.appUsageItems.reduce((acum, item) => {
				const infoSessions = item.data;
				acum.infoSessions = acum.infoSessions || {};
				acum.infoSessions.browsers = acum.infoSessions.browsers || {};
				acum.infoSessions.totalSessions = acum.infoSessions.totalSessions || 0;
				acum.infoSessions.totalSessions += infoSessions.length;

				acum.infoSessions.descriptor = 'Sessions';
				acum.infoSessions.days = days;
				acum.infoSessions.textSessions = `${acum.infoSessions.totalSessions} different sessions in ${days} different days.`;

				acum.releaseNotes = acum.releaseNotes || {};
				acum.releaseNotes.descriptor = 'Release Notes';
				acum.releaseNotes.text = acum.releaseNotes.text || ' - ';

				acum.downloadDocuments = acum.downloadDocuments || {};
				acum.downloadDocuments.descriptor = 'Download Packages';
				acum.downloadDocuments.text = acum.downloadDocuments.text || ' - ';

				acum.openSeries = acum.openSeries || {};
				acum.openSeries.descriptor = 'Visited Series';
				acum.openSeries.text = acum.openSeries.text || ' - ';

				acum.generalFilters = acum.generalFilters || families.reduce((array, f) => array.concat({
					family: f.title,
					text: `${f.title}: Displayed: ON - Discontinued: ON - Updates: OFF`,
				}), []);
				acum.generalFilters.descriptor = 'General View Filters';

				acum.productFilters = acum.productFilters || [];
				acum.productFilters.descriptor = 'Product View Filters';
				acum.productFilters.text = acum.productFilters.text || ' - ';

				acum.documents = acum.documents || {};
				acum.documents.descriptor = 'Documents';
				acum.documents.text = acum.documents.text || ' - ';

				infoSessions.forEach((is) => {
					acum.infoSessions.browsers[is.browser] = acum.infoSessions.browsers[is.browser] ? ++acum.infoSessions.browsers[is.browser] : 1;
					acum.infoSessions.textBrowsers = `\nBrowser: ${Object.entries(acum.infoSessions.browsers).reduce((str, e) => str.concat(`${e[0]}: ${e[1]}\n`), '')}`;

					acum.infoSessions.text = acum.infoSessions.textSessions.concat(acum.infoSessions.textBrowsers);

					is.actions.forEach(({ actionName, actionData }) => {
						switch (actionName) {
							case CONSTANTS.UserSettings.fieldsKeys.releaseNotes:
								acum.releaseNotes.dateTutorialCompleted = actionData.dateTutorialCompleted;
								acum.releaseNotes.maxReleaseVisited = acum.releaseNotes.maxReleaseVisited || 0;
								acum.releaseNotes.maxReleaseVisited = (acum.releaseNotes.maxReleaseVisited < (Object.keys(actionData).length - 2) ? (Object.keys(actionData).length - 2) : acum.releaseNotes.maxReleaseVisited);

								acum.releaseNotes.text = `Tutorial completed: ${acum.releaseNotes.dateTutorialCompleted ? `Yes. Date: ${dayjs(acum.releaseNotes.dateTutorialCompleted).format('YYYY.MM.DD')}` : `No. Max pages visited: ${acum.releaseNotes.maxReleaseVisited}`}.`;
								break;

							case CONSTANTS.UserSettings.fieldsKeys.generalFilters:
								// eslint-disable-next-line no-case-declarations
								const { displayDiscontinuedProducts, isDisplayed, searchForUpdates, title } = actionData.familyInfo,
									family = acum.generalFilters.find((f) => f.family === title);

								family.text = `${title}: Displayed: ${isDisplayed ? 'ON' : 'OFF'} - Discontinued: ${displayDiscontinuedProducts ? 'ON' : 'OFF'} - Updates: ${searchForUpdates ? 'ON' : 'OFF'}`;

								acum.generalFilters.text = acum.generalFilters.reduce((str, f) => str.concat(`${f.text}\n`), '');
								break;

							case CONSTANTS.UserSettings.fieldsKeys.documentLinksFilter:
								if (actionData.okButtonClicked) {
									acum.productFilters.allLanguagesSelected = actionData.allLanguagesSelected;
									acum.productFilters.documentationLanguagesDisplayedTitle = actionData.documentationLanguagesDisplayedTitle;
									acum.productFilters.subcategoriesIdsDisplayed = actionData.subcategoriesDisplayed;
								}

								if (actionData.subcategoryQuickFilter) {
									if (acum.productFilters.subcategoriesIdsDisplayed) {
										acum.productFilters.subcategoriesIdsDisplayed = acum.productFilters.subcategoriesIdsDisplayed.filter((sc) => sc !== actionData.id);
									} else {
										acum.productFilters.subcategoriesIdsDisplayed = documentationSubcategories.filter((sc) => sc !== actionData.id).map((sc) => sc.id);
									}
								}

								if (actionData.categoryQuickFilter) {
									const subcategoriesId = documentationSubcategories.filter((sc) => sc.category.id === actionData.id).map((sc) => sc.id);
									if (acum.productFilters.subcategoriesIdsDisplayed) {
										acum.productFilters.subcategoriesIdsDisplayed = acum.productFilters.subcategoriesIdsDisplayed.filter((sc) => !subcategoriesId.includes(sc));
									} else {
										acum.productFilters.subcategoriesIdsDisplayed = documentationSubcategories.filter((sc) => !subcategoriesId.includes(sc.id)).map((sc) => sc.id);
									}
								}
								acum.productFilters.textLanguages = 'Languages: All\n';
								acum.productFilters.textSubcategories = 'Sub/categories: All\n';
								acum.productFilters.textDefaultPackages = 'Predefined package: None';
								acum.productFilters.title = '';
								if (acum.productFilters.allLanguagesSelected !== undefined) acum.productFilters.textLanguages = `Languages: ${acum.productFilters.allLanguagesSelected ? 'ALL' : acum.productFilters.documentationLanguagesDisplayedTitle}\n`;

								if (acum.productFilters.subcategoriesIdsDisplayed !== undefined) {
									acum.productFilters.textSubcategories = `Sub/categories: ${acum.productFilters.subcategoriesIdsDisplayed.length === documentationSubcategories.length ? 'ALL' : acum.productFilters.subcategoriesIdsDisplayed.length}\n`;


									if (acum.productFilters.subcategoriesIdsDisplayed.length !== documentationSubcategories.length) {
										acum.productFilters.title = `${acum.productFilters.subcategoriesIdsDisplayed.map((scd) => documentationSubcategories.find((sc) => sc.id === scd).fullTitle).sort().join('\n')}`;

										const defaultPackage = documentationPackages.find((dp) => JSON.stringify(dp.subcategoriesIds.sort()) === JSON.stringify(acum.productFilters.subcategoriesIdsDisplayed.sort()));
										acum.productFilters.textDefaultPackages = `Predefined package: ${defaultPackage ? defaultPackage.title : 'None'}\n`;
									}
								}

								acum.productFilters.text = acum.productFilters.textLanguages.concat(acum.productFilters.textSubcategories.concat(acum.productFilters.textDefaultPackages));
								break;

							case CONSTANTS.UserSettings.fieldsKeys.showHistoryVersion:
								acum.documents.totalDocumentsHistory = acum.documents.totalDocumentsHistory || 0;
								acum.documents.totalDocumentsHistory++;
								acum.documents.documentsHistoryName = acum.documents.documentsHistoryName || [];
								if (!acum.documents.documentsHistoryName.includes(actionData.documentName)) acum.documents.documentsHistoryName.push(actionData.documentName);

								acum.documents.textHistory = `VersionHistory: ${acum.documents.totalDocumentsHistory} documents, ${acum.documents.documentsHistoryName.length} different`;

								if (!acum.documents.textDocuments) acum.documents.text = acum.documents.textHistory;
								else acum.documents.text = acum.documents.textHistory.concat(`\n${acum.documents.textDocuments}`);
								break;

							case CONSTANTS.UserSettings.fieldsKeys.favorite:
							case CONSTANTS.UserSettings.fieldsKeys.editUserNoteComment:
							case CONSTANTS.UserSettings.fieldsKeys.copyLinkToClipboard:
							case CONSTANTS.UserSettings.fieldsKeys.editDocumentLink:
								break;

							case CONSTANTS.UserSettings.fieldsKeys.downloadDocuments:
								acum.downloadDocuments.totalPackages = acum.downloadDocuments.totalPackages === undefined ? 1 : ++acum.downloadDocuments.totalPackages;
								acum.downloadDocuments.totalDocuments = acum.downloadDocuments.totalDocuments || 0;
								acum.downloadDocuments.totalDocuments += actionData.length;

								acum.downloadDocuments.text = `${acum.downloadDocuments.totalPackages} packages containing ${acum.downloadDocuments.totalDocuments} documents in total`;
								break;

							case CONSTANTS.UserSettings.fieldsKeys.openPDF:
								acum.documents.totalDocuments = acum.documents.totalDocuments || 0;
								acum.documents.totalDocuments++;
								acum.documents.documentsName = acum.documents.documentsName || [];
								if (!acum.documents.documentsName.includes(actionData.documentName)) acum.documents.documentsName.push(actionData.documentName);

								acum.documents.textDocuments = `Opened: ${acum.documents.totalDocuments} documents, ${acum.documents.documentsName.length} different`;

								if (!acum.documents.textHistory) acum.documents.text = acum.documents.textDocuments;
								else acum.documents.text = acum.documents.textDocuments.concat(`\n${acum.documents.textHistory}`);
								break;

							case CONSTANTS.UserSettings.fieldsKeys.openSeries:
								// eslint-disable-next-line no-case-declarations, one-var
								const seriesVersionText = `${series.find((s) => s.id === actionData.seriesId).title} - ${versions.find((v) => v.id === actionData.versionId).title}`;
								acum.openSeries.totalVisited = acum.openSeries.totalVisited || 0;
								acum.openSeries.totalVisited++;
								acum.openSeries.infoVisited = acum.openSeries.infoVisited || [];

								if (!acum.openSeries.infoVisited.includes(seriesVersionText)) acum.openSeries.infoVisited.push(`${series.find((s) => s.id === actionData.seriesId).title} - ${versions.find((v) => v.id === actionData.versionId).title}`);

								acum.openSeries.text = `Visited: ${acum.openSeries.infoVisited.length} series`;

								acum.openSeries.title = `${acum.openSeries.infoVisited.sort((a, b) => a.localeCompare(b)).join('\n')}`;
								break;

							default:
								break;
						}
					});
				});
				return acum;
			}, {});

		return Object.values(prova);
	}
}


class UserDocumentComment {
	constructor(data) {
		this.text = data.text;
		this.dateYMD = data.dateYMD;
	}
}

class UserDocumentNote {
	constructor(data) {
		this.documentName = data.documentName;
		this.favoriteSeriesIds = data.favoriteSeriesIds;
		this.comment = data.comment ? new UserDocumentComment(data.comment) : null;
	}
}

class UserNoteItem {
	constructor(data) {
		this.id = data.Id;
		this.email = data.Title;
		this.userId = data.UserId;
		this.documentsNotes = JSON.parse(data.JSON).documentsNotes.map((dn) => new UserDocumentNote(dn)); // Object.keys(json).map((dn) => new documentsNotes(json[dn], dn));
	}
}

export {
	AppsUsageItem, AppUsageAction, AppUsageSession,
	DisplayedLanguage, DocumentationLibrary, DocumentationPackage, DocumentationLinkItem, DocumentationLinksList,
	DocumentCategory, DocumentCertification, DocumentationCertificationsPackages, DocumentLanguage, DocumentSubcategory, DocumentVersion,
	ProductFamily, ProductSeries, ProductSeriesStatus, ProductVersion,
	SPUser, UserDocumentComment, UserDocumentNote, UserNoteItem,
};
